import { Component, OnInit, Output, EventEmitter } from "@angular/core";
import {
  AttachmentPoint,
  AttachmentPointType,
} from "../../models/attachmentpoint";
import { AttachmentPointFilter } from "../../models/filters/attachmentpoints-filter";
import {
  AttachmentPointService,
  StationLink,
} from "../../services/attachment-point.service";
import { PaginationInstance } from "ngx-pagination";
import {
  NgbActiveModal,
  NgbModalRef,
  NgbModal,
} from "@ng-bootstrap/ng-bootstrap";
import { AttachmentpointsDeleteComponent } from "../attachmentpoints-delete/attachmentpoints-delete.component";
import { Station } from "../../models/station";
import { AttachmentpointsFormComponent } from "../attachmentpoints-form/attachmentpoints-form.component";
import { environment } from "../../../environments/environment";
import { AttachmentpointsMultipleDeleteComponent } from "../attachmentpoints-multiple-delete/attachmentpoints-multiple-delete.component";
import { AttachmentpointsGroupbindingComponent } from "../attachmentpoints-groupbinding/attachmentpoints-groupbinding.component";
import { AttachmentpointsStationBindingComponent } from "../attachmentpoints-station-binding/attachmentpoints-station-binding.component";
import { filter } from "rxjs/operators";
import { AccountService } from "../../services/account.service";
import { Roles } from "../../models/roles";
import { fadeInAndOut } from "../../utils/animations-utils";
import { ToastsManager } from "ng2-toastr";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-attachmentpoints",
  animations: [fadeInAndOut()],
  templateUrl: "./attachmentpoints-list.component.html",
  styleUrls: ["./attachmentpoints-list.component.css"],
})
export class AttachmentPointsListComponent implements OnInit {
  isSuperAdmin: boolean = false;

  p: number = 1; // current page
  total: number; // total count of records
  itemsPerPage = environment.itemPerPage; // number of records to display on each page
  listLoading: boolean;

  displayNulls: boolean = true;
  filters: Map<string, any>;
  typeFilter: string = "ALL";
  listFiltered = false;
  downloading: boolean = false;

  /**
   * Keys : string criterias
   * Values : boolean => true if ascending, false otherwise
   */
  sortingCriterias: Map<string, boolean> = new Map();

  /** Attachment points to display on the table */
  attachmentPoints: AttachmentPoint[] = [];

  /** Modal windows */
  apToModify: AttachmentPoint;
  apToDelete: AttachmentPoint;
  deleteModal: NgbModalRef;
  formModal: NgbModalRef;
  multipleDeleteModal: NgbModalRef;
  groupBindingModal: NgbModalRef;
  stationBindingModal: NgbModalRef;

  /** Selected APs */
  selectedAPs: Map<AttachmentPoint, boolean> = new Map();
  selectAllChecked: boolean = false;

  constructor(
    private apService: AttachmentPointService,
    private modalService: NgbModal,
    public accountService: AccountService,
    public toast: ToastsManager,
    public translate: TranslateService
  ) {}

  /**
   * Loads first page of results ordered by ID
   */
  ngOnInit() {
    this.initFilters();
    if (localStorage.getItem("itemsPerPage")) {
      this.itemsPerPage = parseInt(localStorage.getItem("itemsPerPage"));
    }
    this.getPage(1);
  }

  /**
   * Create a new empty object for filtering attachment points
   * Retrieve first page
   */
  clearFilters() {
    this.initFilters();
    this.typeFilter = "ALL";
    this.listFiltered = false;
    this.displayNulls = true;
    this.reloadList();
  }

  /**
   * Sorting system function as follow
   * On first click on a criteria => add the criteria as ascending
   * On second click => put the critiera to descending
   * On third click => remove the criteria from the map
   */
  updateSortingCriterias(criteria: string) {
    if (this.sortingCriterias.has(criteria)) {
      if (this.sortingCriterias.get(criteria)) {
        // criteria is present as true => switch to false
        this.sortingCriterias.set(
          criteria,
          !this.sortingCriterias.get(criteria)
        );
      } else {
        // criteria present as false => delete it
        this.sortingCriterias.delete(criteria);
      }
    } else {
      this.sortingCriterias.set(criteria, true);
    }
  }

  initFilters() {
    this.filters = new Map();
  }

  changeItemCount(value) {
    this.itemsPerPage = value;
    this.reloadList();

    localStorage.setItem("itemsPerPage", value);
  }

  sortBy(criteria: string) {
    this.updateSortingCriterias(criteria);
    this.updateFilters(); // take count of the last filters modifications
    this.reloadList();
  }

  onTypeChange() {
    this.updateFilters();
    this.reloadList();
  }

  updateFilters() {
    if (this.typeFilter == "ALL") {
      this.filters["ap.type"] = undefined;
    } else {
      this.filters["ap.type"] = this.typeFilter;
    }

    if (this.typeFilter == "ALL") {
      this.filters["ap.type"] = undefined;
    } else {
      this.filters["ap.type"] = this.typeFilter;
    }
  }

  /**
   * Reload lists according to filters, sorting criterias from the first page
   */
  reloadList() {
    this.listFiltered = false;
    Object.values(this.filters).forEach((value) => {
      if (value) {
        this.listFiltered = true;
      }
    });
    this.getPage(1);
  }

  getPage(page: number) {
    this.p = page;
    this.listLoading = true;
    this.apService
      .getPaginatedFilteredSortedAttachmentPoints(
        this.p,
        this.itemsPerPage,
        this.filters,
        this.sortingCriterias,
        this.displayNulls ? StationLink.ALL : StationLink.ONLY_LINKED
      )
      .subscribe((data) => {
        this.attachmentPoints = data.res;
        this.total = data.headers.get("x-total-count");
        this.listLoading = false;
      });
  }

  onNewClick(): void {
    this.formModal = this.modalService.open(AttachmentpointsFormComponent, {
      size: "lg",
      backdrop: "static",
    });
    this.formModal.componentInstance.creationMode = true;
    this.formModal.componentInstance.attachmentPoint = new AttachmentPoint();
    this.formModal.componentInstance.onCreateValidation.subscribe(() =>
      this.getPage(this.p)
    );
  }

  onUpdateClick(ap) {
    this.apToModify = ap;
    this.formModal = this.modalService.open(AttachmentpointsFormComponent, {
      size: "lg",
      backdrop: "static",
    });
    this.formModal.componentInstance.creationMode = false;
    this.formModal.componentInstance.attachmentPoint = JSON.parse(
      JSON.stringify(this.apToModify)
    );
    this.formModal.componentInstance.onUpdateValidation.subscribe(() =>
      this.getPage(this.p)
    );
    //this.formModal.componentInstance.onUpdateValidation.subscribe(updatedAccount => this.accountHasBeenUpdated(updatedAccount)); */
  }

  onDeleteClick(ap) {
    this.apToDelete = ap;
    this.deleteModal = this.modalService.open(AttachmentpointsDeleteComponent, {
      centered: true,
    });
    this.deleteModal.componentInstance.attachmentPoint = this.apToDelete;
    this.deleteModal.componentInstance.onDeleteValidation.subscribe(
      (deletedAP) => this.apHasBeenDeleted(ap)
    );
  }

  apHasBeenDeleted(ap) {
    this.getPage(this.p);
  }

  onCheckboxChange(ap: AttachmentPoint) {
    if (!this.selectedAPs.get(ap)) {
      this.selectAP(ap);
    } else {
      this.deselectAP(ap);
    }
  }

  selectAP(ap: AttachmentPoint) {
    this.selectedAPs.set(ap, true);
  }

  deselectAP(ap: AttachmentPoint) {
    this.selectedAPs.delete(ap);
    this.selectAllChecked = false;
  }

  onCheckboxSelectALlAPChange() {
    if (this.selectedAPs.size < this.attachmentPoints.length) {
      this.attachmentPoints.forEach((ap) => this.selectedAPs.set(ap, true));
    } else {
      this.selectedAPs.clear();
    }
  }

  bindToGroup() {
    if (this.selectedAPs.size > 0) {
      this.groupBindingModal = this.modalService.open(
        AttachmentpointsGroupbindingComponent,
        {
          size: "lg",
          backdrop: "static",
        }
      );
      this.groupBindingModal.componentInstance.attachmentPoints = Array.from(
        this.selectedAPs.keys()
      );
    }
  }

  bindToStation() {
    if (this.selectedAPs.size > 0) {
      this.stationBindingModal = this.modalService.open(
        AttachmentpointsStationBindingComponent,
        {
          size: "lg",
          backdrop: "static",
        }
      );
      this.stationBindingModal.componentInstance.attachmentPoints = Array.from(
        this.selectedAPs.keys()
      );
    }
  }

  deleteSelection() {
    if (this.selectedAPs.size > 0) {
      this.multipleDeleteModal = this.modalService.open(
        AttachmentpointsMultipleDeleteComponent,
        {
          size: "lg",
          backdrop: "static",
        }
      );
      this.multipleDeleteModal.componentInstance.attachmentPoints = Array.from(
        this.selectedAPs.keys()
      );
    }
  }

  downloadXLSFile() {
    this.downloading = true;
    this.apService
      .downloadXLSFile(
        this.p,
        this.itemsPerPage,
        this.filters,
        this.sortingCriterias,
        this.displayNulls ? StationLink.ALL : StationLink.ONLY_LINKED
      )
      .subscribe((isTruncated) => {
        if (isTruncated) {
          this.toast.warning(this.translate.instant("common.downloadOk"));
        } else {
          this.toast.success(
            this.translate.instant("common.downloadTruncated")
          );
        }
        this.downloading = false;
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/attachmentpoints/attachmentpoints-list/attachmentpoints-list.component.ts